/**
 * Push Notification Subscription Helper
 *
 * Used by the GameBoy PWA to register for push notifications so the
 * doorbell rings the resident's phone even when the app is closed.
 *
 * Flow:
 *   1. User taps "Activar notificaciones" → permission dialog
 *   2. Subscribe via PushManager (needs Service Worker registered)
 *   3. POST subscription to /push/register with auth token
 *   4. Server stores in push_subscriptions table
 *   5. Backend sendDoorbellPush() finds subscriptions and sends Web Push
 */

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

/**
 * Convert a base64 URL-safe string to a Uint8Array (required for applicationServerKey).
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Check if push notifications are supported in this browser.
 */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Get the current notification permission state.
 */
export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

/**
 * Request notification permission from the user.
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied';
  const result = await Notification.requestPermission();
  return result;
}

/**
 * Subscribe the current device to push notifications.
 * Returns the subscription if successful, null otherwise.
 */
export async function subscribeToPush(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;

  const permission = await requestNotificationPermission();
  if (permission !== 'granted') return null;

  // Wait for the service worker to be ready
  const registration = await navigator.serviceWorker.ready;

  // Check existing subscription
  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    return existing;
  }

  // Subscribe with VAPID key
  const options: PushSubscriptionOptionsInit = {
    userVisibleOnly: true,
  };

  if (VAPID_PUBLIC_KEY) {
    options.applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource;
  }

  try {
    const subscription = await registration.pushManager.subscribe(options);
    return subscription;
  } catch (err) {
    console.error('[Push] Subscribe failed:', err);
    return null;
  }
}

/**
 * Send the subscription to the server.
 * Returns {ok, status, error} so the caller can show useful messages
 * in the UI. Pure {ok:false} loses too much info for debugging.
 */
export async function registerSubscriptionOnServer(
  subscription: PushSubscription,
  token: string,
  apiBase: string
): Promise<{ ok: boolean; status?: number; error?: string }> {
  try {
    const json = subscription.toJSON();
    const p256dh = json.keys?.p256dh;
    const auth = json.keys?.auth;
    if (!json.endpoint || !p256dh || !auth) {
      console.error('[Push] Subscription missing keys');
      return { ok: false, error: 'subscription_incomplete' };
    }

    const res = await fetch(`${apiBase}/push/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        endpoint: json.endpoint,
        keys: { p256dh, auth },
      }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const errText = body?.error || `HTTP ${res.status}`;
      console.error('[Push] Server register failed:', res.status, body);
      return { ok: false, status: res.status, error: errText };
    }
    return { ok: true };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'network_error';
    console.error('[Push] Register error:', err);
    return { ok: false, error: msg };
  }
}

/**
 * Unsubscribe from push AND remove the subscription from the server.
 */
export async function unsubscribeAndRemoveFromServer(
  token: string,
  apiBase: string
): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      await fetch(`${apiBase}/push/unregister`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ endpoint }),
      });
    } else {
      await fetch(`${apiBase}/push/unregister`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
    }
    return true;
  } catch (err) {
    console.error('[Push] Unsubscribe failed:', err);
    return false;
  }
}

/**
 * Register the service worker. Call once on app boot.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    return registration;
  } catch (err) {
    console.error('[SW] Register failed:', err);
    return null;
  }
}